import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  Shield, ArrowLeft, User, MapPin, Hash, Save, CheckCircle
} from 'lucide-react';
import AppFooter from '../components/AppFooter';

export default function ProfilePage() {
  const { user, updateProfile, logout } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || '');
  const [address, setAddress] = useState(user?.address || '');
  const [previousAddress, setPreviousAddress] = useState(user?.previous_address || '');
  const [ssnLast4, setSsnLast4] = useState(user?.ssn_last4 || '');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (ssnLast4 && (ssnLast4.length !== 4 || !/^\d{4}$/.test(ssnLast4))) {
      setError('SSN must be exactly 4 digits');
      return;
    }

    setLoading(true);
    const result = await updateProfile({
      name,
      address,
      previous_address: previousAddress,
      ssn_last4: ssnLast4
    });

    if (result.success) {
      setSuccess(true);
      toast.success('Profile saved successfully!', {
        description: 'Your information has been updated and will appear on your dispute letters.'
      });
      setTimeout(() => setSuccess(false), 3000);
    } else {
      setError(result.error);
      toast.error('Failed to save profile', { description: result.error });
    }
    setLoading(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="profile-page">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Shield className="w-8 h-8 text-[#B76E79]" strokeWidth={1.5} />
              <span className="ml-2 font-['Playfair_Display'] text-xl font-semibold text-[#36454F]">
                Selah Slate&trade;
              </span>
            </div>
            <Button 
              variant="ghost" 
              onClick={handleLogout}
              className="text-[#36454F]/70 hover:text-[#36454F] hover:bg-[#36454F]/5 font-['Manrope']"
              data-testid="logout-btn"
            >
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Link */}
        <Link 
          to="/dashboard" 
          className="inline-flex items-center text-[#36454F]/65 hover:text-[#36454F] font-['Manrope'] mb-6 transition-colors"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
          Back to Dashboard
        </Link>

        <Card className="border-[#36454F]/15 bg-white animate-fade-in-up">
          <CardHeader>
            <CardTitle className="font-['Playfair_Display'] text-2xl text-[#36454F] flex items-center">
              <User className="w-6 h-6 mr-3 text-[#B76E79]" strokeWidth={1.5} />
              Your Profile
            </CardTitle>
            <CardDescription className="font-['Manrope'] text-[#36454F]/65">
              This information will appear on your FCRA dispute letters
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <div className="mb-6 p-4 bg-[#D9534F]/10 border border-[#D9534F]/30 text-[#D9534F] text-sm font-['Manrope']" data-testid="profile-error">
                {error}
              </div>
            )}

            {success && (
              <div className="mb-6 p-4 bg-[#4CAF50]/10 border border-[#4CAF50]/30 text-[#4CAF50] text-sm font-['Manrope'] flex items-center" data-testid="profile-success">
                <CheckCircle className="w-5 h-5 mr-2" strokeWidth={1.5} />
                Profile updated successfully
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-[#36454F] font-['Manrope'] font-medium flex items-center">
                  <User className="w-4 h-4 mr-2 text-[#B76E79]" strokeWidth={1.5} />
                  Full Legal Name
                </Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  className="h-12 border-[#36454F]/15 focus:border-[#B76E79] focus:ring-[#B76E79] rounded-sm font-['Manrope']"
                  required
                  data-testid="profile-name-input"
                />
                <p className="text-xs text-[#36454F]/50 font-['Manrope']">
                  Use your full legal name as it appears on official documents
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address" className="text-[#36454F] font-['Manrope'] font-medium flex items-center">
                  <MapPin className="w-4 h-4 mr-2 text-[#B76E79]" strokeWidth={1.5} />
                  Current Address
                </Label>
                <Input
                  id="address"
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="123 Main St, City, State 12345"
                  className="h-12 border-[#36454F]/15 focus:border-[#B76E79] focus:ring-[#B76E79] rounded-sm font-['Manrope']"
                  required
                  data-testid="profile-address-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="previousAddress" className="text-[#36454F] font-['Manrope'] font-medium flex items-center">
                  <MapPin className="w-4 h-4 mr-2 text-[#36454F]/40" strokeWidth={1.5} />
                  Previous Address (Optional)
                </Label>
                <Input
                  id="previousAddress"
                  type="text"
                  value={previousAddress}
                  onChange={(e) => setPreviousAddress(e.target.value)}
                  placeholder="456 Old St, City, State 54321"
                  className="h-12 border-[#36454F]/15 focus:border-[#B76E79] focus:ring-[#B76E79] rounded-sm font-['Manrope']"
                  data-testid="profile-previous-address-input"
                />
                <p className="text-xs text-[#36454F]/50 font-['Manrope']">
                  Include if you've moved in the last 2 years
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ssn" className="text-[#36454F] font-['Manrope'] font-medium flex items-center">
                  <Hash className="w-4 h-4 mr-2 text-[#B76E79]" strokeWidth={1.5} />
                  Last 4 Digits of SSN
                </Label>
                <Input
                  id="ssn"
                  type="text"
                  value={ssnLast4}
                  onChange={(e) => setSsnLast4(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="1234"
                  maxLength={4}
                  className="h-12 border-[#36454F]/15 focus:border-[#B76E79] focus:ring-[#B76E79] rounded-sm font-['Manrope'] w-32"
                  required
                  data-testid="profile-ssn-input"
                />
                <p className="text-xs text-[#36454F]/50 font-['Manrope']">
                  Required for credit bureau identification. Encrypted and secure.
                </p>
              </div>

              <div className="pt-4 border-t border-[#36454F]/10">
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] font-medium rounded-sm transition-all duration-300"
                  data-testid="profile-save-btn"
                >
                  {loading ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center">
                      <Save className="w-5 h-5 mr-2" strokeWidth={1.5} />
                      Save Profile
                    </span>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <p className="mt-6 text-center text-xs text-[#36454F]/50 font-['Manrope']">
          Your data is encrypted at rest and never shared with third parties.
        </p>
      </main>
      <AppFooter />
    </div>
  );
}
